import { clsx } from 'clsx';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

export type BadgeColor = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' | 'teal';
export type BadgeSize = 'sm' | 'md' | 'lg';
export type BadgeFill = 'subtle' | 'bright';

// Figma 8318:44071 — per-colour token quartet (text / fill / fill-bright / border).
// Values are mode-invariant by design: badge surfaces are locked-light tints with locked-dark
// text in both light and dark modes (see the Case 4 convention in index.css). `teal` is a
// non-Figma extension kept for the existing callsites; it follows the same shape against our
// brand palette.
const tokens: Record<BadgeColor, { text: string; fill: string; fillBright: string; border: string }> = {
    gray: {
        text: '#4a5565',
        fill: '#fafafa',
        fillBright: '#f3f4f6',
        border: '#6a72821a',
    },
    red: {
        text: '#c10007',
        fill: '#fef2f2',
        fillBright: '#ffe2e2',
        border: '#e7000b1a',
    },
    yellow: {
        text: '#894b00',
        fill: '#fefce8',
        fillBright: '#fef9c2',
        border: '#d0870033',
    },
    green: {
        text: '#008236',
        fill: '#f0fdf4',
        fillBright: '#dcfce7',
        border: '#00a63e33',
    },
    blue: {
        text: '#1447e6',
        fill: '#eff6ff',
        fillBright: '#dbeafe',
        border: '#1447e61a',
    },
    indigo: {
        text: '#432dd7',
        // Figma only shipped fill-2 for indigo; reuse it for both fill levels so the colour stays
        // consistent rather than falling back to plain neutral-50.
        fill: '#e0e7ff',
        fillBright: '#e0e7ff',
        border: '#432dd71a',
    },
    purple: {
        text: '#8200db',
        fill: '#faf5ff',
        fillBright: '#f3e8ff',
        border: '#8200db1a',
    },
    pink: {
        text: '#c6005c',
        fill: '#fdf2f8',
        fillBright: '#fce7f3',
        border: '#c6005c1a',
    },
    // Non-Figma extension — uses our brand-teal palette. Follows the same token shape so the
    // size/fill/close variant logic Just Works.
    teal: {
        text: '#0d9488',
        fill: '#f0fdfa',
        fillBright: '#ccfbf1',
        border: '#0d94881a',
    },
};

const sizeChrome: Record<BadgeSize, string> = {
    // SM — Figma radius-md (6) + 6×2 padding + 12px text. Maps to Tailwind `rounded-sm` since
    // our `--radius-sm` is overridden to 6px in index.css.
    sm: 'rounded-sm px-1.5 py-0.5 text-xs font-medium',
    // MD — same radius, 8×4 padding, 14px text (replaces the legacy style="square" branch).
    md: 'rounded-sm px-2 py-1 text-sm font-medium',
    // LG — Figma radius-lg (8). Tailwind's `rounded-lg` is 10px in our override — closest
    // standard step to Figma's 8px and matches the legacy style="shadow" / className="rounded-md"
    // visual size that several callsites were emulating.
    lg: 'rounded-lg px-2.5 py-1.5 text-sm font-medium',
};

const closeSize: Record<BadgeSize, string> = {
    sm: 'size-3',
    md: 'size-3.5',
    lg: 'size-4',
};

interface BadgeProps {
    /**
     * Badge colour. Backend GraphQL fragments type `badgeColor: string` (see
     * `AitLevelConfig`, `BurnoutInventoryCategoryAverage`, etc.) — callsites cast as
     * BadgeColor without runtime narrowing. Unknown values fall back to `gray` so
     * a misclassified backend payload renders inertly instead of crashing.
     */
    color: BadgeColor;
    text: string;
    size?: BadgeSize;
    fill?: BadgeFill;
    /** Custom leading icon (e.g. a lucide icon). */
    icon?: ReactNode;
    /**
     * When set, renders the Figma "with close" trailing X — a clickable handler is invoked
     * separately from the badge's own onClick, so the badge stays clickable as a chip while
     * the X can dismiss it.
     */
    onClose?: () => void;
    onClick?: () => void;
    className?: string;
    /**
     * Escape hatch for data-driven palettes (e.g. PCS zone labels, dynamic radar items)
     * whose colours don't fit the 9-colour set. When passed, overrides the token lookup.
     */
    customColor?: { text: string; fill: string; border?: string };
}

const Badge = ({
    color,
    text,
    size = 'sm',
    fill = 'subtle',
    icon,
    onClose,
    onClick,
    className,
    customColor,
}: BadgeProps) => {
    // Defensive fallback — see `color` prop doc above.
    const t = tokens[color] ?? tokens.gray;
    const bgHex = customColor?.fill ?? (fill === 'bright' ? t.fillBright : t.fill);
    const textHex = customColor?.text ?? t.text;
    const borderHex = customColor?.border ?? t.border;

    return (
        <span
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                onClick
                    ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            style={{
                backgroundColor: bgHex,
                color: textHex,
                boxShadow: `inset 0 0 0 1px ${borderHex}`,
            }}
            className={clsx(
                'inline-flex items-center gap-1.5',
                sizeChrome[size],
                onClick &&
                    'cursor-pointer select-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 focus-visible:outline-none',
                className
            )}
        >
            {icon && <span className="inline-flex shrink-0 items-center">{icon}</span>}
            {text}
            {onClose && (
                <button
                    type="button"
                    aria-label="Remove"
                    onClick={e => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="inline-flex shrink-0 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-current focus-visible:outline-none"
                >
                    <X className={closeSize[size]} strokeWidth={2} />
                </button>
            )}
        </span>
    );
};

export default Badge;
